import React, { useState, useEffect, useRef } from 'react';
import { LetterFormData, LetterGenerationResponse } from '../../types';
import { DocumentTemplate } from '../../types/document';
import { EmailRecipient } from '../../types/email';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaMicrophone, FaFileAlt, FaCopy, FaDownload, FaSave } from 'react-icons/fa';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import PatientSearchDropdown from '../../components/PatientSearchDropdown';
import DocumentEditor from '../../components/DocumentEditor';
import RecipientSelector from '../../components/RecipientSelector';

const GenerateLetter: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dictationId = searchParams.get('dictationId');
  const { toast, showToast, hideToast } = useToast();
  const editorRef = useRef<any>(null);
  
  const [showEmptyPromptAlert, setShowEmptyPromptAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentDictation, setCurrentDictation] = useState<any>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [patientDetails, setPatientDetails] = useState<any>(null);
  const [providerDetails, setProviderDetails] = useState<any>(null);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [toRecipients, setToRecipients] = useState<EmailRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>([]);
  
  const [formData, setFormData] = useState<LetterFormData>({
    letterTitle: '',
    letterDate: new Date().toLocaleDateString('en-AU').split('/').join('/'),
    clinicianName: '',
    patientName: '',
    patientDob: '',
    patientGender: '',
    signOff: 'Yours sincerely',
    instructions: '',
    prompt: '',
  });
  
  const [generatedLetter, setGeneratedLetter] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [aiStatus, setAiStatus] = useState<'idle' | 'sending' | 'waiting' | 'streaming' | 'error'>('idle');

  // Fetch document templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!token) return;
      
      try {
        const response = await apiCall('/correspondence/document-templates/?is_active=true', {
          method: 'GET',
        }, token);
        
        if (response.ok) {
          const data = await response.json();
          const templatesList = data.results || data || [];
          setTemplates(templatesList);
          
          // Restore last selected template from localStorage
          const lastTemplateId = localStorage.getItem('lastSelectedLetterTemplate');
          if (lastTemplateId && templatesList.some((t: DocumentTemplate) => t.id === lastTemplateId)) {
            setSelectedTemplateId(lastTemplateId);
            console.log('[GenerateLetter] Restored last selected template:', lastTemplateId);
          }
        }
      } catch (error) {
        console.error('[GenerateLetter] Error fetching templates:', error);
      }
    };
    
    fetchTemplates();
  }, [token]);

  // Load dictation data and auto-transcribe when dictationId is present
  useEffect(() => {
    const loadDictationAndTranscribe = async () => {
      if (!dictationId || !token) return;
      
      try {
        console.log('[GenerateLetter] Loading dictation:', dictationId);
        const response = await apiCall(`/letters/dictations/${dictationId}/`, {
          method: 'GET',
        }, token);
        
        if (response.ok) {
          const dictation = await response.json();
          console.log('[GenerateLetter] Loaded dictation:', dictation);
          setCurrentDictation(dictation);
          
          // Populate form with dictation data
          setFormData(prev => ({
            ...prev,
            letterTitle: dictation.dictation_type || '',
            patientName: dictation.patient_name || '',
            patientDob: dictation.patient_dob ? new Date(dictation.patient_dob).toLocaleDateString('en-AU') : '',
            patientGender: dictation.patient_gender || '',
            clinicianName: dictation.user_name || '',
          }));
          
          // Set patient ID if dictation has associated patient
          if (dictation.patient) {
            setSelectedPatientId(dictation.patient);
          }
          
          // Load generated letter if available
          if (dictation.generated_letter) {
            setGeneratedLetter(dictation.generated_letter);
            console.log('[GenerateLetter] Loaded existing generated letter from Letter model');
          } else if (dictation.status === 'completed') {
            // For backward compatibility: try to fetch from Document if dictation is completed but has no Letter
            console.log('[GenerateLetter] No Letter model found, checking for Document...');
            await fetchGeneratedLetterFromDocument(dictation.dictation_reference);
          }
          
          // Auto-transcribe the audio if not already transcribed
          if (dictation.audio_file && dictation.status !== 'transcribed' && dictation.status !== 'completed') {
            await transcribeDictation(dictationId);
          } else if (dictation.status === 'transcribed' || dictation.status === 'completed') {
            // Fetch existing transcription
            await fetchExistingTranscription(dictationId);
          }
        } else {
          console.error('[GenerateLetter] Failed to load dictation:', response.status);
        }
      } catch (error) {
        console.error('[GenerateLetter] Error loading dictation:', error);
      }
    };
    
    loadDictationAndTranscribe();
  }, [dictationId, token]);
  
  const fetchExistingTranscription = async (id: string) => {
    try {
      console.log('[GenerateLetter] Fetching existing transcription');
      const response = await apiCall(
        ENDPOINTS.DICTATIONS.TRANSCRIBE(Number(id)),
        { method: 'POST' },
        token || undefined
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.text) {
          setFormData(prev => ({
            ...prev,
            prompt: data.text
          }));
          if (data.cached) {
            showToast('Loaded existing transcription', 'success');
          }
        }
      }
    } catch (error) {
      console.error('[GenerateLetter] Error fetching transcription:', error);
    }
  };
  
  const transcribeDictation = async (id: string) => {
    setIsTranscribing(true);
    showToast('Transcribing audio to text...', 'info');
    
    try {
      console.log('[GenerateLetter] Starting transcription for dictation:', id);
      const response = await apiCall(
        ENDPOINTS.DICTATIONS.TRANSCRIBE(Number(id)),
        { method: 'POST' },
        token || undefined
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('[GenerateLetter] Transcription response:', data);
        
        if (data.success && data.text) {
          // Auto-populate the prompt field with transcribed text
          setFormData(prev => ({
            ...prev,
            prompt: data.text
          }));
          showToast('Audio transcribed successfully!', 'success');
        } else {
          showToast(data.error || 'Failed to transcribe audio', 'error');
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.error || 'Transcription failed', 'error');
      }
    } catch (error) {
      console.error('[GenerateLetter] Error transcribing:', error);
      showToast('Error during transcription', 'error');
    } finally {
      setIsTranscribing(false);
    }
  };
  
  const fetchGeneratedLetterFromDocument = async (dictationReference: string) => {
    if (!dictationReference || !token) return;
    
    try {
      console.log('[GenerateLetter] Fetching document for dictation reference:', dictationReference);
      // Search for document by dictation_reference
      const response = await apiCall(
        `/documents/?dictation_reference=${encodeURIComponent(dictationReference)}`,
        { method: 'GET' },
        token
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          const document = data.results[0];
          console.log('[GenerateLetter] Found document:', document.id);
          
          // Fetch the document content
          const contentResponse = await apiCall(
            `/documents/${document.id}/content/`,
            { method: 'GET' },
            token
          );
          
          if (contentResponse.ok) {
            const contentData = await contentResponse.json();
            if (contentData.content) {
              setGeneratedLetter(contentData.content);
              console.log('[GenerateLetter] Loaded letter from Document (backward compatibility)');
            }
          }
        } else {
          console.log('[GenerateLetter] No document found for this dictation');
        }
      }
    } catch (error) {
      console.error('[GenerateLetter] Error fetching document:', error);
    }
  };
  
  // Fetch patient details when patient is selected
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (!selectedPatientId || !token) return;
      
      try {
        const response = await apiCall(
          `/patients/${selectedPatientId}/`,
          { method: 'GET' },
          token
        );
        
        if (response.ok) {
          const patient = await response.json();
          setPatientDetails(patient);
          console.log('[GenerateLetter] Patient details loaded:', patient);
        }
      } catch (error) {
        console.error('[GenerateLetter] Error fetching patient details:', error);
      }
    };
    
    fetchPatientDetails();
  }, [selectedPatientId, token]);
  
  // Fetch provider details (current user)
  useEffect(() => {
    const fetchProviderDetails = async () => {
      if (!token) return;
      
      try {
        // Fetch current user's provider profile
        const response = await apiCall(
          `/providers/`,
          { method: 'GET' },
          token
        );
        
        if (response.ok) {
          const data = await response.json();
          // Assuming current user is in the list, or we'll fetch by user ID
          if (data.results && data.results.length > 0) {
            // For now, take first provider (ideally match by user)
            const provider = data.results[0];
            setProviderDetails(provider);
            console.log('[GenerateLetter] Provider details loaded:', provider);
          }
        }
      } catch (error) {
        console.error('[GenerateLetter] Error fetching provider details:', error);
      }
    };
    
    fetchProviderDetails();
  }, [token]);
  
  // Load template when selected and auto-populate variables when form data changes
  useEffect(() => {
    const loadAndPopulateTemplate = async () => {
      if (!selectedTemplateId || !token) {
        // If no template selected, clear editor to blank document
        return;
      }
      
      if (!editorRef.current?.documentEditor) {
        console.log('[GenerateLetter] Editor not ready yet');
        return;
      }
      
      try {
        console.log('[GenerateLetter] Loading template:', selectedTemplateId);
        const response = await apiCall(
          `/correspondence/document-templates/${selectedTemplateId}/`,
          { method: 'GET' },
          token
        );
        
        if (response.ok) {
          const template: DocumentTemplate = await response.json();
          console.log('[GenerateLetter] Template loaded:', template.name);
          
          if (template.content_template && editorRef.current?.documentEditor) {
            // Load template first
            editorRef.current.documentEditor.open(template.content_template);
            console.log('[GenerateLetter] Template loaded into editor');
            
            // Now do live replacement using DocumentEditor API (handles fragmented text)
            const editor = editorRef.current.documentEditor;
            
            // Prepare replacements
            const today = new Date();
            const dateShort = formData.letterDate || today.toLocaleDateString('en-AU');
            
            // Format long date with ordinal suffix
            const dateLong = (() => {
              if (!formData.letterDate) return '';
              const parts = formData.letterDate.split('/');
              if (parts.length !== 3) return formData.letterDate;
              const day = parseInt(parts[0]);
              const month = parseInt(parts[1]) - 1;
              const year = parseInt(parts[2]);
              const getOrdinalSuffix = (n: number) => {
                const s = ['th', 'st', 'nd', 'rd'];
                const v = n % 100;
                return s[(v - 20) % 10] || s[v] || s[0];
              };
              const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
              return `${day}${getOrdinalSuffix(day)} ${monthNames[month]}, ${year}`;
            })();
            
            // Build full address from recipient
            const recipient = toRecipients[0];
            const recipientAddress = recipient ? [
              recipient.address_line_1,
              recipient.address_line_2,
              [recipient.suburb, recipient.state, recipient.postcode].filter(Boolean).join(' '),
              recipient.country
            ].filter(Boolean).join(', ') : '';
            
            // Build patient address from patient details
            const patientAddress = patientDetails ? [
              patientDetails.address_line_1,
              patientDetails.address_line_2,
              [patientDetails.suburb, patientDetails.state, patientDetails.postcode].filter(Boolean).join(' '),
              patientDetails.country
            ].filter(Boolean).join(', ') : '';
            
            // Calculate patient age from DOB
            const calculateAge = (dobString: string): string => {
              if (!dobString) return '';
              try {
                // Parse DD/MM/YYYY format
                const parts = dobString.split('/');
                if (parts.length !== 3) return '';
                const day = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1; // Month is 0-indexed
                const year = parseInt(parts[2]);
                const birthDate = new Date(year, month, day);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                  age--;
                }
                return age.toString();
              } catch {
                return '';
              }
            };
            
            const patientAge = calculateAge(formData.patientDob || patientDetails?.date_of_birth);
            
            const replacements: { [key: string]: string } = {
              // Date fields
              '[LetterDateShort]': dateShort,
              '[LetterDateLong]': dateLong,
              // Patient fields from formData and patientDetails
              '[PatientSalutation]': patientDetails?.salutation || '',
              '[PatientFirstName]': patientDetails?.first_name || '',
              '[PatientLastName]': patientDetails?.last_name || '',
              '[PatientFullName]': formData.patientName || (patientDetails ? `${patientDetails.salutation || ''} ${patientDetails.first_name || ''} ${patientDetails.last_name || ''}`.trim() : ''),
              '[PatientName]': formData.patientName,
              '[PatientDOB]': formData.patientDob,
              '[PatientAge]': patientAge,
              '[PatientAddress]': patientAddress,
              '[PatientPhone]': patientDetails?.phone_number || '',
              '[PatientMobile]': patientDetails?.mobile_number || '',
              // Provider fields from formData and providerDetails
              '[ProviderSalutation]': providerDetails?.salutation || 'Dr',
              '[ProviderFirstName]': providerDetails?.first_name || '',
              '[ProviderLastName]': providerDetails?.last_name || '',
              '[ProviderFullName]': formData.clinicianName ? `Dr ${formData.clinicianName}` : (providerDetails?.full_name || ''),
              '[ProviderName]': formData.clinicianName || providerDetails?.full_name || '',
              '[ProviderTitle]': providerDetails?.credentials || '',
              '[ProviderSpecialty]': providerDetails?.specializations_details?.[0]?.name || '',
              '[ProviderPhone]': providerDetails?.phone || '',
              '[ProviderEmail]': providerDetails?.email || '',
              // Recipient fields - ALL contact fields
              '[RecipientSalutation]': recipient?.salutation || '',
              '[RecipientFirstName]': recipient?.first_name || '',
              '[RecipientLastName]': recipient?.last_name || '',
              '[RecipientFullName]': recipient?.name || '',
              '[RecipientOrganization]': recipient?.company || recipient?.organization || '',
              '[RecipientAddress]': recipientAddress,
              '[RecipientEmail]': recipient?.email || '',
              '[RecipientPhone]': recipient?.phone || '',
              '[RecipientMobile]': recipient?.mobile || '',
              '[RecipientFax]': recipient?.fax || '',
            };
            
            console.log('[GenerateLetter] Replacing placeholders live...');
            console.log('[GenerateLetter] Patient Details:', patientDetails);
            console.log('[GenerateLetter] Provider Details:', providerDetails);
            console.log('[GenerateLetter] Recipient:', recipient);
            
            // Use DocumentEditor's search/replace API (handles fragmented text correctly)
            for (const [placeholder, value] of Object.entries(replacements)) {
              if (value) {  // Only replace if there's an actual value
                try {
                  editor.search.findAll(placeholder);
                  const results = editor.search.searchResults;
                  if (results && results.length > 0) {
                    console.log(`[GenerateLetter] Replacing ${placeholder} → "${value}" (${results.length} times)`);
                    editor.search.searchResults.replaceAll(value);
                  }
                } catch (e) {
                  console.log(`[GenerateLetter] Could not replace ${placeholder}:`, e);
                }
              }
            }
            
            console.log('[GenerateLetter] Live replacements complete');
          }
        }
      } catch (error) {
        console.error('[GenerateLetter] Error loading template:', error);
        showToast('Failed to load template', 'error');
      }
    };
    
    loadAndPopulateTemplate();
  }, [selectedTemplateId, token, formData.letterDate, formData.clinicianName, formData.patientName, formData.patientDob, toRecipients, patientDetails, providerDetails]);
  
  const handlePatientSelect = (patientId: string, patientName: string, patientDob?: string, patientGender?: string) => {
    setSelectedPatientId(patientId);
    
    // PatientSearchDropdown already provides DOB and gender, use them directly
    setFormData(prev => ({
      ...prev,
      patientName: patientName,
      patientDob: patientDob || '',
      patientGender: (patientGender as '' | 'male' | 'female') || ''
    }));
  };

  const formatDate = (input: string): string => {
    let value = input.replace(/\D/g, '');
    if (value.length > 8) value = value.substr(0, 8);
    
    if (value.length >= 4) {
      const day = value.substr(0, 2);
      const month = value.substr(2, 2);
      const year = value.substr(4);
      value = day + '/' + month + (year ? '/' + year : '');
    }
    else if (value.length >= 2) {
      value = value.substr(0, 2) + '/' + value.substr(2);
    }
    return value;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.prompt.trim()) {
      setShowEmptyPromptAlert(true);
      setGeneratedLetter('');
      return;
    }

    if (!token) {
      setError('Authentication required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setAiStatus('sending');
      
      // Save patient details to dictation before generating letter
      if (dictationId && selectedPatientId) {
        console.log('[GenerateLetter] Saving patient details to dictation...');
        const updateDictationResponse = await apiCall(
          `/letters/dictations/${dictationId}/update_patient/`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              patient_id: selectedPatientId,
              dictation_type: formData.letterTitle,
            }),
          },
          token
        );
        
        if (!updateDictationResponse.ok) {
          console.warn('Failed to update dictation with patient info');
        } else {
          console.log('[GenerateLetter] Patient details saved to dictation');
        }
      }
      
      const requestData = {
        letter_date: formData.letterDate,
        clinician_name: formData.clinicianName,
        patient_name: formData.patientName,
        patient_dob: formData.patientDob,
        patient_gender: formData.patientGender,
        sign_off: formData.signOff,
        instructions: formData.instructions,
        prompt: formData.prompt,
        template_id: selectedTemplateId || null,
      };
      
      console.log('[GenerateLetter] Sending request to AI...');
      console.log('[GenerateLetter] Endpoint:', ENDPOINTS.LETTERS.GENERATE);
      console.log('[GenerateLetter] Request data:', {
        ...requestData,
        prompt: `${requestData.prompt.substring(0, 100)}... (${requestData.prompt.length} chars)`
      });
      
      const response = await apiCall(
        ENDPOINTS.LETTERS.GENERATE,
        {
          method: 'POST',
          body: JSON.stringify(requestData),
        },
        token
      );

      console.log('[GenerateLetter] Request sent, awaiting response...');
      console.log('[GenerateLetter] Response status:', response.status);
      setAiStatus('waiting');

      const data: LetterGenerationResponse = await response.json();
      
      console.log('[GenerateLetter] Response received:', data.success);
      console.log('[GenerateLetter] Template ID sent:', selectedTemplateId);
      console.log('[GenerateLetter] Letter content type:', typeof data.letter_content);
      console.log('[GenerateLetter] Letter content preview:', data.letter_content?.substring(0, 200));
      setAiStatus('streaming');
      
      if (data.success) {
        setGeneratedLetter(data.letter_content);
        
        // Load the generated content into the document editor
        if (editorRef.current?.documentEditor) {
          try {
            // If template_id was provided, backend returns blended SFDT
            // Otherwise, it returns plain HTML
            if (selectedTemplateId) {
              // Backend returned SFDT with variables replaced
              console.log('[GenerateLetter] Loading blended SFDT into editor...');
              editorRef.current.documentEditor.open(data.letter_content);
              console.log('[GenerateLetter] Loaded blended template into editor');
            } else {
              // No template - convert HTML to SFDT
              const plainDocument = {
                sections: [
                  {
                    blocks: [
                      {
                        paragraphFormat: { textAlignment: 'Left' },
                        characterFormat: {},
                        inlines: [{ text: data.letter_content, characterFormat: {} }]
                      }
                    ],
                    headersFooters: {}
                  }
                ]
              };
              editorRef.current.documentEditor.open(JSON.stringify(plainDocument));
              console.log('[GenerateLetter] Loaded plain letter into editor');
            }
          } catch (error) {
            console.error('[GenerateLetter] Error loading letter into editor:', error);
          }
        }
        
        setAiStatus('idle');
        showToast('Letter generated successfully', 'success');
      } else {
        setError(data.error || 'Failed to generate letter');
        setAiStatus('error');
        showToast(data.error || 'Failed to generate letter', 'error');
      }
    } catch (err) {
      console.error('[GenerateLetter] Error:', err);
      setError('An error occurred while generating the letter. Please try again.');
      setAiStatus('error');
      showToast('An error occurred while generating the letter', 'error');
    } finally {
      setIsLoading(false);
      if (aiStatus !== 'idle') {
        setTimeout(() => setAiStatus('idle'), 3000);
      }
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedLetter);
  };

  const handleDownload = () => {
    if (editorRef.current?.documentEditor) {
      // Use Syncfusion's built-in DOCX export
      // NOTE: Do NOT include .docx extension - Syncfusion adds it automatically
      const fileName = `letter_${formData.patientName}_${formData.letterDate.replace(/\//g, '-')}`;
      editorRef.current.documentEditor.save(fileName, 'Docx');
    } else {
      // Fallback if editor not available
      const blob = new Blob([generatedLetter], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `letter_${formData.patientName}_${formData.letterDate.replace(/\//g, '-')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleSaveToDocuments = async () => {
    if (!generatedLetter || !currentDictation) {
      showToast('No letter to save or dictation not found', 'error');
      return;
    }

    // Check selectedPatientId OR currentDictation.patient
    const patientId = selectedPatientId || currentDictation.patient;
    if (!patientId) {
      showToast('Please associate a patient with this dictation first', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      // Step 1: Update dictation with patient info if patient was selected
      if (selectedPatientId && selectedPatientId !== currentDictation.patient) {
        const updateDictationResponse = await apiCall(
          `/letters/dictations/${dictationId}/`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              patient: selectedPatientId,
            }),
          },
          token || undefined
        );
        
        if (!updateDictationResponse.ok) {
          showToast('Failed to update dictation with patient info', 'error');
          setIsSaving(false);
          return;
        }
      }

      // Step 2: Create document from letter
      // Get SFDT content from editor if available, otherwise use HTML
      let documentContent = generatedLetter;
      if (editorRef.current?.documentEditor) {
        try {
          documentContent = editorRef.current.documentEditor.serialize();
          console.log('[GenerateLetter] Using SFDT from editor for document');
        } catch (error) {
          console.error('[GenerateLetter] Error serializing editor content:', error);
          console.log('[GenerateLetter] Falling back to HTML content');
        }
      }
      
      const response = await apiCall(
        ENDPOINTS.DOCUMENTS.CREATE_FROM_LETTER,
        {
          method: 'POST',
          body: JSON.stringify({
            patient_id: patientId,
            content: documentContent,
            dictation_id: dictationId,
            dictation_reference: currentDictation.dictation_reference,
            provider_id: currentDictation.user, // clinician who created dictation
            title: `Draft - ${formData.letterTitle || 'Medical Letter'}`,
          }),
        },
        token || undefined
      );

      if (response.ok) {
        const documentData = await response.json();
        const documentId = documentData.id;
        console.log('[GenerateLetter] Document created with ID:', documentId);

        // Step 3: Save generated letter to dictation's Letter model
        const saveLetterResponse = await apiCall(
          `/letters/dictations/${dictationId}/save_letter/`,
          {
            method: 'POST',
            body: JSON.stringify({
              content: generatedLetter,
              title: formData.letterTitle || 'Medical Letter',
              patient_name: formData.patientName,
            }),
          },
          token || undefined
        );

        if (!saveLetterResponse.ok) {
          console.warn('Failed to save letter to dictation, but document was created');
        }

        // Step 4: Mark dictation as completed
        const markCompleteResponse = await apiCall(
          ENDPOINTS.DICTATIONS.MARK_COMPLETED(Number(dictationId)),
          {
            method: 'POST',
          },
          token || undefined
        );

        if (markCompleteResponse.ok) {
          showToast('Letter saved to Documents and dictation marked complete', 'success');
          setTimeout(() => {
            navigate('/documents', { 
              state: { 
                patientId: patientId,
                patientName: formData.patientName,
                patientDob: formData.patientDob,
                documentId: documentId  // Pass the newly created document ID
              } 
            });
          }, 2000);
        } else {
          showToast('Letter saved, but failed to mark dictation complete', 'warning');
        }
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to save document', 'error');
      }
    } catch (error) {
      console.error('[GenerateLetter] Error saving to documents:', error);
      showToast('Error saving document', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="m-2 md:m-4 p-2 md:p-4 bg-base-100 rounded-3xl" style={{ marginTop: '0.5rem' }}>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />

      <div className="flex justify-between items-start mb-4 gap-6">
        {/* Title */}
        <div className="flex-1">
          {/* Letter Title */}
          <div className="flex items-center gap-3 mb-3" style={{ maxWidth: '700px' }}>
            <label className="label py-1 min-w-[100px]">
              <span className="label-text font-semibold">Letter Title</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Letter to GP, Medical Report..."
              className="input input-bordered input-sm flex-1"
              value={formData.letterTitle}
              onChange={(e) => setFormData({
                ...formData,
                letterTitle: e.target.value
              })}
            />
          </div>
          
          {/* To Recipients */}
          <div className="flex items-start gap-3 mb-3" style={{ maxWidth: '700px' }}>
            <label className="label py-1 min-w-[100px] pt-2">
              <span className="label-text font-semibold">To</span>
            </label>
            <div className="flex-1">
              <RecipientSelector
                label=""
                selectedRecipients={toRecipients}
                onChange={setToRecipients}
                placeholder="Search contacts (GPs, lawyers, WorkCover agents)..."
                allowMultiple={true}
                compact={true}
                includePatients={false}
                requireEmail={false}
              />
            </div>
          </div>
          
          {/* CC Recipients */}
          <div className="flex items-start gap-3 mb-3" style={{ maxWidth: '700px' }}>
            <label className="label py-1 min-w-[100px] pt-2">
              <span className="label-text font-semibold">CC</span>
            </label>
            <div className="flex-1">
              <RecipientSelector
                label=""
                selectedRecipients={ccRecipients}
                onChange={setCcRecipients}
                placeholder="Search contacts for CC..."
                allowMultiple={true}
                compact={true}
                includePatients={false}
                requireEmail={false}
              />
            </div>
          </div>
        </div>

        {/* Patient Details - Compact */}
        <div className="card bg-base-200 shadow-md flex-shrink-0 rounded-lg" style={{ minWidth: '400px' }}>
          <div className="card-body p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text text-xs font-semibold">Letter Date *</span>
                </label>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  className="input input-bordered input-sm"
                  value={formData.letterDate}
                  onChange={(e) => setFormData({
                    ...formData,
                    letterDate: formatDate(e.target.value)
                  })}
                  maxLength={10}
                  required
                />
              </div>

              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text text-xs font-semibold">Clinician Name *</span>
                </label>
                <input
                  type="text"
                  placeholder="Dr. John Smith"
                  className="input input-bordered input-sm"
                  value={formData.clinicianName}
                  onChange={(e) => setFormData({
                    ...formData,
                    clinicianName: e.target.value
                  })}
                  required
                />
              </div>

              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text text-xs font-semibold">Patient Name *</span>
                </label>
                {currentDictation?.patient ? (
                  <input
                    type="text"
                    className="input input-bordered input-sm"
                    value={formData.patientName}
                    readOnly
                    title="Patient pre-selected from dictation"
                  />
                ) : (
                  <PatientSearchDropdown
                    value={formData.patientName}
                    onChange={handlePatientSelect}
                  />
                )}
              </div>

              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text text-xs font-semibold">Patient DOB *</span>
                </label>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  className="input input-bordered input-sm"
                  value={formData.patientDob}
                  onChange={(e) => setFormData({
                    ...formData,
                    patientDob: formatDate(e.target.value)
                  })}
                  maxLength={10}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Letter Template */}
        <div className="card bg-base-200 shadow-md flex-shrink-0 rounded-lg" style={{ minWidth: '400px' }}>
          <div className="card-body p-4">
            <div className="form-control">
              <label className="label py-1">
                <span className="label-text text-xs font-semibold">Letter Template</span>
              </label>
              <select
                className="select select-bordered"
                value={selectedTemplateId}
                onChange={(e) => {
                  const newTemplateId = e.target.value;
                  setSelectedTemplateId(newTemplateId);
                  // Persist to localStorage
                  if (newTemplateId) {
                    localStorage.setItem('lastSelectedLetterTemplate', newTemplateId);
                  } else {
                    localStorage.removeItem('lastSelectedLetterTemplate');
                  }
                }}
              >
                <option value="">No template (blank letter)</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Two Panel Layout - Transcript and Generated Letter */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Transcript */}
          <div className="card bg-base-200 shadow-xl rounded-lg lg:col-span-1">
            <div className="card-body p-4">
              <h2 className="card-title text-base-content mb-2">
                <FaMicrophone className="text-primary" />
                Transcript / Letter Content
                {isTranscribing && (
                  <span className="badge badge-info gap-2">
                    <span className="loading loading-spinner loading-xs"></span>
                    Transcribing audio...
                  </span>
                )}
              </h2>
              <textarea
                className="textarea textarea-bordered w-full h-[380px] font-mono text-sm"
                placeholder={isTranscribing ? "Transcribing audio to text..." : "Enter the transcript or describe the letter content here..."}
                value={formData.prompt}
                onChange={(e) => setFormData({
                  ...formData,
                  prompt: e.target.value
                })}
                disabled={isTranscribing}
                required
              />
              
              <div className="card-actions justify-end mt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                >
                  {aiStatus === 'sending' && 'Sending to AI...'}
                  {aiStatus === 'waiting' && 'Awaiting Response...'}
                  {aiStatus === 'streaming' && 'Receiving Letter...'}
                  {(aiStatus === 'idle' || aiStatus === 'error') && (isLoading ? 'Generating...' : 'Generate Letter with AI')}
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Generated Letter */}
          <div className="card bg-base-200 shadow-xl rounded-lg lg:col-span-2">
            <div className="card-body p-4">
              <h2 className="card-title text-base-content flex-shrink-0 mb-2">
                <FaFileAlt className="text-success" />
                Generated Letter
                {aiStatus === 'sending' && (
                  <span className="badge badge-info gap-2">
                    <span className="loading loading-spinner loading-xs"></span>
                    Sending to AI...
                  </span>
                )}
                {aiStatus === 'waiting' && (
                  <span className="badge badge-warning gap-2">
                    <span className="loading loading-spinner loading-xs"></span>
                    Awaiting AI response...
                  </span>
                )}
                {aiStatus === 'streaming' && (
                  <span className="badge badge-success gap-2">
                    <span className="loading loading-spinner loading-xs"></span>
                    Streaming response...
                  </span>
                )}
              </h2>
              
              <div style={{ height: '380px' }}>
                <DocumentEditor
                  ref={editorRef}
                  height="380px"
                  isReadOnly={false}
                  enableToolbar={false}
                  showPropertiesPane={false}
                />
              </div>

              {generatedLetter && (
                <div className="card-actions justify-end mt-2">
                  <button
                    type="button"
                    onClick={handleCopyToClipboard}
                    className="btn btn-outline gap-2"
                  >
                    <FaCopy />
                    Copy
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="btn btn-success gap-2"
                  >
                    <FaDownload />
                    Download
                  </button>
                  {dictationId && (
                    <button
                      type="button"
                      onClick={handleSaveToDocuments}
                      disabled={isSaving}
                      className={`btn btn-primary gap-2 ${isSaving ? 'loading' : ''}`}
                    >
                      <FaSave />
                      {isSaving ? 'Saving...' : 'Save to Documents'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Alert Modal */}
      {showEmptyPromptAlert && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Empty Content Description</h3>
            <p className="py-4">No information to generate a letter</p>
            <div className="modal-action">
              <button
                onClick={() => setShowEmptyPromptAlert(false)}
                className="btn btn-primary"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateLetter;
